/**
 * Client side of the `/api/ai-ask` SSE stream.
 *
 *   const abort = new AbortController();
 *   for await (const evt of streamAsk({ question }, abort.signal)) {
 *     if (evt.type === 'token') ...
 *   }
 *
 * Yields typed `AskEvent`s as the server emits them. Throws on HTTP
 * errors before the stream starts. After the stream starts, errors are
 * delivered as `{ type: 'error' }` events.
 */

import type { AskEvent, AskRequest, AskSourceAyah } from '@/types/ai';

export async function* streamAsk(
  body: AskRequest,
  signal?: AbortSignal,
): AsyncGenerator<AskEvent, void, void> {
  const res = await fetch('/api/ai-ask', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });

  if (!res.ok || !res.body) {
    // Non-streaming error envelope, matching our `ApiResponse` shape.
    let message = `HTTP ${res.status}`;
    try {
      const errorBody = (await res.json()) as {
        ok?: false;
        error?: { code?: string; message?: string };
      };
      message = errorBody.error?.message ?? message;
      yield {
        type: 'error',
        code: errorBody.error?.code ?? 'http',
        message,
      };
    } catch {
      yield { type: 'error', code: 'http', message };
    }
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // SSE events are separated by blank lines.
      let eventEnd: number;
      while ((eventEnd = buffer.indexOf('\n\n')) >= 0) {
        const block = buffer.slice(0, eventEnd);
        buffer = buffer.slice(eventEnd + 2);
        const parsed = parseSseBlock(block);
        if (parsed) yield parsed;
      }
    }
    // Flush trailing event (some proxies don't terminate with a blank line).
    if (buffer.trim()) {
      const parsed = parseSseBlock(buffer);
      if (parsed) yield parsed;
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {
      // ignore
    }
  }
}

function parseSseBlock(block: string): AskEvent | null {
  let eventName: string | null = null;
  let dataLine = '';
  for (const rawLine of block.split('\n')) {
    const line = rawLine.trimEnd();
    if (line.startsWith('event: ')) eventName = line.slice(7).trim();
    else if (line.startsWith('data: ')) dataLine += line.slice(6);
  }
  if (!eventName) return null;
  let payload: Record<string, unknown> = {};
  if (dataLine) {
    try {
      payload = JSON.parse(dataLine) as Record<string, unknown>;
    } catch {
      return null;
    }
  }
  switch (eventName) {
    case 'token':
      return { type: 'token', text: String(payload.text ?? '') };
    case 'sources':
      return {
        type: 'sources',
        sources: (payload.sources as AskSourceAyah[] | undefined) ?? [],
      };
    case 'verification':
      return {
        type: 'verification',
        invalid_citations:
          (payload.invalid_citations as string[] | undefined) ?? [],
      };
    case 'done':
      return { type: 'done' };
    case 'error':
      return {
        type: 'error',
        code: String(payload.code ?? 'unknown'),
        message: String(payload.message ?? 'Unknown error'),
      };
    default:
      return null;
  }
}
